import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface CurrencyContextType {
  currency: string;
  currencySymbol: string;
  currencyDisplay: 'symbol' | 'code' | 'both';
  formatCurrency: (amount: number, decimals?: number) => string;
  formatCurrencySimple: (amount: number, decimals?: number) => string;
  updateCurrencyPreferences: (prefs: Partial<CurrencyPreferences>) => void;
}

interface CurrencyPreferences {
  currency: string;
  currencySymbol: string;
  currencyDisplay: 'symbol' | 'code' | 'both';
}

const defaultPreferences: CurrencyPreferences = {
  currency: 'ZAR',
  currencySymbol: 'R',
  currencyDisplay: 'symbol'
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<CurrencyPreferences>(defaultPreferences);

  // Update preferences when user changes
  useEffect(() => {
    if (user?.preferences) {
      setPreferences({
        currency: user.preferences.currency || 'ZAR',
        currencySymbol: user.preferences.currencySymbol || 'R',
        currencyDisplay: user.preferences.currencyDisplay || 'symbol'
      });
    }
  }, [user]);

  const formatCurrency = (amount: number, decimals: number = 2): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `${preferences.currencySymbol} 0.00`;
    }
    
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
    
    switch (preferences.currencyDisplay) {
      case 'code':
        return `${preferences.currency} ${formatted}`;
      case 'both':
        return `${preferences.currencySymbol} ${formatted} (${preferences.currency})`;
      case 'symbol':
      default:
        return `${preferences.currencySymbol} ${formatted}`;
    }
  };

  const formatCurrencySimple = (amount: number, decimals: number = 2): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `${preferences.currencySymbol}0.00`;
    }
    
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
    
    switch (preferences.currencyDisplay) {
      case 'code':
        return `${preferences.currency}${formatted}`;
      case 'both':
        return `${preferences.currencySymbol}${formatted} (${preferences.currency})`;
      case 'symbol':
      default:
        return `${preferences.currencySymbol}${formatted}`;
    }
  };

  const updateCurrencyPreferences = (newPrefs: Partial<CurrencyPreferences>) => {
    setPreferences(prev => ({ ...prev, ...newPrefs }));
  };

  const value: CurrencyContextType = {
    currency: preferences.currency,
    currencySymbol: preferences.currencySymbol,
    currencyDisplay: preferences.currencyDisplay,
    formatCurrency,
    formatCurrencySimple,
    updateCurrencyPreferences
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
